import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Camera } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { ROLES, photoSrc } from "./playerHelpers";
import { DatePicker } from "../ui/date-picker";

const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

function PhotoUploader({ photo, onChange }) {
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_SIZE) {
      toast.error("File troppo grande (max 5MB)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Foto caricata");
    } catch {
      toast.error("Upload fallito");
    }
  };

  return (
    <div className="flex items-center gap-4">
      <label className="h-24 w-24 rounded-2xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center cursor-pointer overflow-hidden hover:bg-slate-50">
        {photo ? (
          <img src={photoSrc(photo)} alt="" className="h-full w-full object-cover" />
        ) : (
          <Camera size={22} className="text-slate-400" />
        )}
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </label>
      <div className="text-sm text-slate-500">
        <div className="font-medium text-slate-700">Foto giocatore</div>
        Carica un'immagine quadrata (max 5 MB).
      </div>
    </div>
  );
}

export default function PlayerFormDialog({
  open,
  onOpenChange,
  form,
  setForm,
  editingId,
  onSubmit,
}) {
  const setField = (field, value) => setForm({ ...form, [field]: value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingId ? "Modifica giocatore" : "Nuovo giocatore"}</DialogTitle>
          <DialogDescription className="sr-only">Compila i dati anagrafici, sportivi e dei contatti del giocatore.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <PhotoUploader
            photo={form.photo}
            onChange={(url) => setField("photo", url)}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setField("first_name", e.target.value)}
                data-testid="player-first-name"
              />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setField("last_name", e.target.value)}
                data-testid="player-last-name"
              />
            </div>
            <div>
              <Label>Data di nascita</Label>
              <DatePicker
                value={form.birth_date}
                onChange={(v) => setField("birth_date", v)}
              />
            </div>
            <div>
              <Label>Numero maglia</Label>
              <Input
                type="number"
                value={form.jersey_number}
                onChange={(e) => setField("jersey_number", e.target.value)}
              />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setField("role", v)}>
                <SelectTrigger data-testid="player-role-select">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Taglia</Label>
              <Input
                value={form.size}
                onChange={(e) => setField("size", e.target.value)}
                placeholder="S, M, L..."
              />
            </div>
            <div>
              <Label>Genitore - Nome</Label>
              <Input
                value={form.parent_name}
                onChange={(e) => setField("parent_name", e.target.value)}
              />
            </div>
            <div>
              <Label>Genitore - Telefono</Label>
              <Input
                value={form.parent_phone}
                onChange={(e) => setField("parent_phone", e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>Genitore - Email</Label>
              <Input
                type="email"
                value={form.parent_email}
                onChange={(e) => setField("parent_email", e.target.value)}
              />
            </div>
            <div>
              <Label>Tessera FIGC</Label>
              <Input
                value={form.figc_card_number || ""}
                onChange={(e) => setField("figc_card_number", e.target.value)}
                placeholder="123456"
                data-testid="player-figc-input"
              />
            </div>
            <div>
              <Label>Matricola FIGC</Label>
              <Input
                value={form.figc_id || ""}
                onChange={(e) => setField("figc_id", e.target.value)}
                placeholder="4631745"
                data-testid="player-figc-id"
              />
            </div>
            <div>
              <Label>Sigla documento</Label>
              <Input
                value={form.figc_document || ""}
                onChange={(e) => setField("figc_document", e.target.value.toUpperCase())}
                placeholder="CRT-SGS"
                maxLength={20}
                data-testid="player-figc-doc"
              />
            </div>
            <div>
              <Label>Codice fiscale</Label>
              <Input
                value={form.fiscal_code || ""}
                onChange={(e) => setField("fiscal_code", e.target.value.toUpperCase())}
                placeholder="RSSMRA10A01H501Z"
                maxLength={16}
              />
            </div>
            <div className="col-span-2">
              <Label>Note mediche</Label>
              <Textarea
                rows={2}
                value={form.medical_notes}
                onChange={(e) => setField("medical_notes", e.target.value)}
                placeholder="Allergie, intolleranze, certificati..."
              />
            </div>
            <div className="col-span-2">
              <Label>Note generali</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={onSubmit}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="save-player-btn"
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
